/**
 * ✅ Approve Corporate Account Modal
 * Approve pending corporate accounts with credit limit
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Business,
  Person,
  Email,
  Phone,
} from '@mui/icons-material';
import {
  CorporateAccount,
  corporateAccountsService,
} from '@/services/corporateAccountsService';
import toast from 'react-hot-toast';

interface ApproveCorporateAccountModalProps {
  open: boolean;
  account: CorporateAccount | null;
  onClose: () => void;
  onApprove: () => void;
}

export const ApproveCorporateAccountModal: React.FC<ApproveCorporateAccountModalProps> = ({
  open,
  account,
  onClose,
  onApprove,
}) => {
  const [creditLimit, setCreditLimit] = useState('25000');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ creditLimit?: string }>({});

  if (!account) return null;

  // Get currency symbol based on account currency
  const currencySymbol = account.currency === 'NGN' ? '₦' : '$';
  const currencyCode = account.currency || 'USD';

  const validateForm = () => {
    const newErrors: { creditLimit?: string } = {};

    const limit = parseFloat(creditLimit);
    if (isNaN(limit) || limit <= 0) {
      newErrors.creditLimit = 'Credit limit must be a positive number';
    } else if (limit > 1000000) {
      newErrors.creditLimit = `Credit limit cannot exceed ${currencySymbol}1,000,000`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await corporateAccountsService.approveCorporateAccount({
        accountId: account.id,
        creditLimit: parseFloat(creditLimit),
        notes: notes || undefined,
      });

      if (response.success) {
        toast.success(`${account.companyName} has been approved successfully`);
        onApprove();
        handleClose();
      } else {
        toast.error(response.error || 'Failed to approve corporate account');
      }
    } catch (error) {
      console.error('Failed to approve account:', error);
      toast.error('Failed to approve corporate account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCreditLimit('25000');
    setNotes('');
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            <Typography variant="h6">Approve Corporate Account</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitting}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          You are about to approve this corporate account. Please set an appropriate credit limit.
        </Alert>

        {/* Account Information */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Business color="primary" fontSize="small" />
            <Typography variant="subtitle2">{account.companyName}</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {account.contactPerson.name}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {account.contactPerson.email}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {account.contactPerson.phone}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Credit Limit Input */}
        <TextField
          fullWidth
          required
          label="Credit Limit"
          type="number"
          value={creditLimit}
          onChange={(e) => setCreditLimit(e.target.value)}
          error={!!errors.creditLimit}
          helperText={errors.creditLimit || 'Set the maximum credit amount for this account'}
          disabled={submitting}
          InputProps={{
            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
          }}
          sx={{ mb: 2 }}
        />

        {/* Suggested Credit Limits */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Quick select:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {[10000, 25000, 50000, 100000].map((amount) => (
              <Button
                key={amount}
                size="small"
                variant="outlined"
                onClick={() => setCreditLimit(amount.toString())}
                disabled={submitting}
              >
                {currencySymbol}{amount.toLocaleString()}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Notes */}
        <TextField
          fullWidth
          label="Notes (Optional)"
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes about this approval..."
          disabled={submitting}
          helperText="Internal notes visible to other admins"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={<CheckCircle />}
        >
          {submitting ? 'Approving...' : 'Approve Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
